import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { 
  Plus, Search, PlayCircle, Clock, Users, Star, ChevronRight, ChevronDown, ChevronUp,
  Check, Lock, BookOpen, Trash2, Pencil, ArrowLeft, BarChart3, Video,
  CheckCircle2, Circle, GripVertical, X, Save, Eye, Settings, Layers,
  Upload, Paperclip, Link2, Shield, ExternalLink, FileText
} from 'lucide-react';
import AuthContext from '../context/AuthContext';
import SelectMenu from '../components/SelectMenu';
import { apiClient } from '../apiClient';

const SORT_OPTIONS = [
  { value: 'updated_desc', label: 'Recently Updated' },
  { value: 'title_asc', label: 'Title A-Z' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' }
];

const LEVEL_OPTIONS = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' }
];

const TIER_OPTIONS = [
  { value: 'starter', label: 'Starter ($17/mo)' },
  { value: 'pro', label: 'Pro ($27/mo)' },
  { value: 'admin', label: 'Admin' }
];

const LESSON_MEDIA_OPTIONS = [
  { value: 'none', label: 'No media' },
  { value: 'video_url', label: 'Video URL (YouTube/Vimeo/file)' },
  { value: 'video_asset', label: 'Upload private video' },
  { value: 'image_url', label: 'Image URL' },
  { value: 'image_asset', label: 'Upload image' }
];

const LESSON_MEDIA_LABELS = {
  none: 'No media',
  video_url: 'Video',
  video_asset: 'Private video',
  image_url: 'Image',
  image_asset: 'Private image'
};

const RESOURCE_LINK_TYPE_OPTIONS = [
  { value: 'external', label: 'External URL' },
  { value: 'project', label: 'Project' },
  { value: 'folder', label: 'Folder' },
  { value: 'prompt', label: 'Prompt' },
  { value: 'agent_instruction', label: 'Agent Instruction' }
];

const INTERNAL_RESOURCE_PATHS = {
  project: (resourceId) => resourceId ? `/projects/${resourceId}` : '',
  folder: (resourceId, projectId) => (resourceId && projectId) ? `/projects/${projectId}/folders/${resourceId}` : '',
  prompt: (resourceId) => resourceId ? `/prompts/${resourceId}` : '',
  agent_instruction: (resourceId) => resourceId ? `/agent-instructions/${resourceId}` : ''
};

const formatPrice = (priceCents, currency = 'USD') => {
  if (!priceCents || priceCents === 0) return 'Free';
  const amount = (priceCents / 100).toFixed(0);
  return `$${amount}`;
};

const formatDuration = (minutes) => {
  if (!minutes) return '';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins ? `${hours}h ${mins}m` : `${hours}h`;
};

const formatFileSize = (bytes) => {
  const value = Number(bytes || 0);
  if (!value || value < 1024) return value ? `${value} B` : '';
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 * 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const makeClientId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const cleanUrl = (value = '') => String(value || '').trim();

const isRenderableImageUrl = (value = '') => {
  const url = cleanUrl(value);
  if (!url) return false;
  if (url.startsWith('data:image/')) return true;
  try {
    const parsed = new URL(url, window.location.origin);
    const hostname = parsed.hostname.toLowerCase();
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.endsWith('.supabase.co') ||
      hostname.endsWith('.rebelprompts.com')
    );
  } catch (_) {
    return false;
  }
};

const normalizeMediaType = (lesson = {}) => {
  const explicit = (lesson.media_type || '').toLowerCase();
  if (LESSON_MEDIA_OPTIONS.some(opt => opt.value === explicit)) return explicit;
  if (lesson.media_path) {
    const contentType = String(lesson.media_content_type || '').toLowerCase();
    if (contentType.startsWith('image/')) return 'image_asset';
    return 'video_asset';
  }
  if (lesson.image_url) return 'image_url';
  if (lesson.video_url) return 'video_url';
  return 'none';
};

const getLessonSignals = (lesson = {}) => {
  const mediaType = normalizeMediaType(lesson);
  const attachments = Array.isArray(lesson.attachments) ? lesson.attachments.length : 0;
  const resources = Array.isArray(lesson.resource_links) ? lesson.resource_links.length : 0;
  const hasText = Boolean(String(lesson.content_md || '').trim());
  return {
    mediaType,
    mediaLabel: LESSON_MEDIA_LABELS[mediaType] || 'Media',
    attachments,
    resources,
    hasText
  };
};

const getInternalResourcePath = (resource = {}) => {
  const type = String(resource.resource_type || '').toLowerCase();
  const resolver = INTERNAL_RESOURCE_PATHS[type];
  if (!resolver) return '';
  return resolver(resource.resource_id, resource.project_id);
};

const getResourceHref = (resource = {}) => {
  const url = cleanUrl(resource.url || '');
  if (url) return url;
  return getInternalResourcePath(resource);
};

const isVideoEmbedUrl = (url = '') => {
  const value = cleanUrl(url).toLowerCase();
  return value.includes('youtube.com') || value.includes('youtu.be') || value.includes('vimeo.com');
};

const toEmbedUrl = (url = '') => {
  const value = cleanUrl(url);
  if (!value) return '';
  try {
    const parsed = new URL(value);
    const host = parsed.hostname.toLowerCase();
    if (host.includes('youtube.com')) {
      const id = parsed.searchParams.get('v');
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (host.includes('youtu.be')) {
      const id = parsed.pathname.replace('/', '');
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (host.includes('vimeo.com')) {
      const id = parsed.pathname.split('/').filter(Boolean).pop();
      if (id) return `https://player.vimeo.com/video/${id}`;
    }
  } catch (_) {
    return value;
  }
  return value;
};

const linkifyText = (value = '') => {
  const lines = String(value || '').split('\n');
  const urlRegex = /(https?:\/\/[^\s]+)/gi;
  return lines.map((line, lineIndex) => {
    const segments = line.split(urlRegex);
    return (
      <React.Fragment key={`line-${lineIndex}`}>
        {segments.map((segment, index) => {
          if (/^https?:\/\//i.test(segment)) {
            return (
              <a key={`seg-${lineIndex}-${index}`} href={segment} target="_blank" rel="noopener noreferrer">
                {segment}
              </a>
            );
          }
          return <React.Fragment key={`seg-${lineIndex}-${index}`}>{segment}</React.Fragment>;
        })}
        {lineIndex < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
};

// ==================== COURSE CARD ====================
function CourseCard({ course, onEdit, onManage, onDelete, isAdmin }) {
  const navigate = useNavigate();
  const coverImageUrl = isRenderableImageUrl(course.cover_image_url) ? cleanUrl(course.cover_image_url) : '';
  
  return (
    <div className="course-card" onClick={() => navigate(`/courses/${course.course_id}`)}>
      <div className="course-card-image">
        {coverImageUrl ? (
          <img src={coverImageUrl} alt={course.title} />
        ) : (
          <div className="course-card-placeholder">
            <BookOpen className="w-8 h-8" />
          </div>
        )}
        {course.price_cents === 0 && (
          <span className="course-card-badge free">Free</span>
        )}
        {course.included_tiers?.length > 0 && course.price_cents > 0 && (
          <span className="course-card-badge included">Included with {course.included_tiers[0]}</span>
        )}
      </div>
      
      <div className="course-card-body">
        <div className="course-card-meta">
          {course.level && <span className="course-level">{course.level}</span>}
          {course.rating_count > 0 && (
            <span className="course-rating">
              <Star className="w-3 h-3" /> {course.rating_avg?.toFixed(1)}
            </span>
          )}
        </div>
        
        <h3 className="course-card-title">{course.title}</h3>
        <p className="course-card-desc">{course.description || 'No description'}</p>
        
        <div className="course-card-footer">
          <div className="course-card-stats">
            {course.enrollment_count > 0 && (
              <span><Users className="w-3 h-3" /> {course.enrollment_count}</span>
            )}
            {course.total_duration_minutes > 0 && (
              <span><Clock className="w-3 h-3" /> {formatDuration(course.total_duration_minutes)}</span>
            )}
          </div>
          <div className="course-card-price">
            {formatPrice(course.price_cents, course.currency)}
          </div>
        </div>
        
        {!course.is_published && (
          <div className="course-card-draft">Draft</div>
        )}
      </div>
      
      {isAdmin && (
        <div className="course-card-actions" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => onEdit(course)} title="Edit"><Pencil className="w-4 h-4" /></button>
          <button onClick={() => onManage(course)} title="Manage Content"><Layers className="w-4 h-4" /></button>
          <button onClick={() => onDelete(course.course_id)} className="danger" title="Delete"><Trash2 className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
}

// ==================== COURSE CATALOG ====================
function CourseCatalog() {
  const { user } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const isAdmin = Boolean(
    user?.permissions?.courses_manage ||
    user?.is_admin ||
    user?.account_level === 'admin'
  );
  
  const [courses, setCourses] = useState([]);
  const [enrolledIds, setEnrolledIds] = useState(new Set());
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('updated_desc');
  const [filter, setFilter] = useState('all'); // all, free, enrolled
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const loadCourses = useCallback(async () => {
    setLoading(true);
    try {
      const [coursesData, enrolledData] = await Promise.all([
        apiClient.get('/api/courses', { search: search || undefined, sort }),
        apiClient.get('/api/users/me/courses/enrolled').catch(() => [])
      ]);
      setCourses(coursesData || []);
      setEnrolledIds(new Set((enrolledData || []).map(c => c.course_id)));
    } catch (err) {
      setError(err.message || 'Unable to load courses');
    } finally {
      setLoading(false);
    }
  }, [search, sort]);
  
  useEffect(() => {
    const timer = setTimeout(loadCourses, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [loadCourses, search]);
  
  const handleDelete = async (courseId) => {
    if (!window.confirm('Delete this course?')) return;
    try {
      await apiClient.del(`/api/courses/${courseId}`);
      setCourses(prev => prev.filter(c => c.course_id !== courseId));
    } catch (err) {
      setError(err.message);
    }
  };
  
  const filteredCourses = courses.filter(course => {
    if (filter === 'free') return course.price_cents === 0;
    if (filter === 'enrolled') return enrolledIds.has(course.course_id);
    return true;
  });
  
  return (
    <div className="courses-page">
      <div className="courses-header">
        <div className="courses-header-content">
          <h1>Courses</h1>
          <p>Master AI prompting with expert-led courses</p>
        </div>
        {isAdmin && (
          <button className="btn-primary" onClick={() => navigate('/courses/new')}>
            <Plus className="w-4 h-4" /> Create Course
          </button>
        )}
      </div>
      
      <div className="courses-toolbar">
        <div className="courses-search">
          <Search className="w-4 h-4" />
          <input
            type="text"
            placeholder="Search courses..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        
        <div className="courses-filters">
          <div className="filter-tabs">
            <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>All</button>
            <button className={filter === 'free' ? 'active' : ''} onClick={() => setFilter('free')}>Free</button>
            <button className={filter === 'enrolled' ? 'active' : ''} onClick={() => setFilter('enrolled')}>My Courses</button>
          </div>
          
          <SelectMenu
            value={sort}
            options={SORT_OPTIONS}
            onChange={setSort}
            compact
          />
        </div>
      </div>
      
      {error && <div className="error-banner">{error}</div>}
      
      {loading ? (
        <div className="courses-loading"><div className="spinner" /></div>
      ) : filteredCourses.length === 0 ? (
        <div className="courses-empty">
          <BookOpen className="w-12 h-12" />
          <h3>No courses found</h3>
          <p>{filter === 'enrolled' ? "You haven't enrolled in any courses yet." : 'Check back soon for new courses.'}</p>
        </div>
      ) : (
        <div className="courses-grid">
          {filteredCourses.map(course => (
            <CourseCard
              key={course.course_id}
              course={course}
              isAdmin={isAdmin}
              onEdit={(c) => navigate(`/courses/${c.course_id}/edit`)}
              onManage={(c) => navigate(`/courses/${c.course_id}/builder`)}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== COURSE DETAIL ====================
function CourseDetail() {
  const { courseId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = React.useContext(AuthContext);
  
  const [course, setCourse] = useState(null);
  const [outline, setOutline] = useState([]);
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [expandedModules, setExpandedModules] = useState({});
  const [checkoutStatus, setCheckoutStatus] = useState('');
  const coverImageUrl = isRenderableImageUrl(course?.cover_image_url) ? cleanUrl(course?.cover_image_url) : '';
  
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [courseData, outlineData, accessData] = await Promise.all([
          apiClient.get(`/api/courses/${courseId}`),
          apiClient.get(`/api/courses/${courseId}/outline`),
          apiClient.get(`/api/courses/${courseId}/access`).catch(() => ({ has_access: false }))
        ]);
        setCourse(courseData);
        setOutline(outlineData?.modules || []);
        setAccess(accessData);
        // Expand first module by default
        if (outlineData?.modules?.length) {
          setExpandedModules({ [outlineData.modules[0].module_id]: true });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [courseId]);

  useEffect(() => {
    const purchaseState = searchParams.get('purchase');
    const sessionId = searchParams.get('session_id');
    if (!purchaseState) return;

    if (purchaseState === 'cancelled') {
      setCheckoutStatus('Checkout canceled. You can try again anytime.');
      const next = new URLSearchParams(searchParams);
      next.delete('purchase');
      next.delete('session_id');
      setSearchParams(next, { replace: true });
      return;
    }

    if (purchaseState !== 'success' || !sessionId) return;

    let cancelled = false;
    const confirmPurchase = async () => {
      setEnrolling(true);
      try {
        await apiClient.post(`/api/courses/${courseId}/checkout/confirm`, { session_id: sessionId });
        if (cancelled) return;
        const accessData = await apiClient.get(`/api/courses/${courseId}/access`).catch(() => ({ has_access: false }));
        if (!cancelled) {
          setAccess(accessData);
          setCheckoutStatus('Payment successful. You now have access to this course.');
        }
      } catch (err) {
        if (!cancelled) {
          setCheckoutStatus(err.message || 'Payment completed but we could not confirm access yet.');
        }
      } finally {
        if (!cancelled) {
          setEnrolling(false);
          const next = new URLSearchParams(searchParams);
          next.delete('purchase');
          next.delete('session_id');
          setSearchParams(next, { replace: true });
        }
      }
    };

    confirmPurchase();
    return () => {
      cancelled = true;
    };
  }, [courseId, searchParams, setSearchParams]);
  
  const handleEnroll = async () => {
    if (access?.has_access) {
      navigate(`/courses/${courseId}/learn`);
      return;
    }

    setEnrolling(true);
    setCheckoutStatus('');
    try {
      if (isFree || tierIncluded) {
        await apiClient.post(`/api/courses/${courseId}/enroll`);
        navigate(`/courses/${courseId}/learn`);
        return;
      }

      const checkout = await apiClient.post(`/api/courses/${courseId}/checkout`, {});
      if (checkout?.checkout_url) {
        window.location.assign(checkout.checkout_url);
        return;
      }
      if (checkout?.status === 'already_has_access') {
        navigate(`/courses/${courseId}/learn`);
        return;
      }
      throw new Error('Unable to start checkout. Please try again.');
    } catch (err) {
      setCheckoutStatus(err.message || 'Unable to start enrollment.');
    } finally {
      setEnrolling(false);
    }
  };
  
  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };
  
  if (loading) {
    return <div className="courses-page"><div className="courses-loading"><div className="spinner" /></div></div>;
  }
  
  if (!course) {
    return (
      <div className="courses-page">
        <div className="courses-empty">
          <h3>Course not found</h3>
          <button className="btn-secondary mt-4" onClick={() => navigate('/courses')}>Back to Courses</button>
        </div>
      </div>
    );
  }
  
  const totalLessons = outline.reduce((sum, m) => sum + (m.lessons?.length || 0), 0);
  const hasAccess = access?.has_access;
  const isFree = course.price_cents === 0;
  const userTier = user?.account_tier || user?.account_level || 'free';
  const tierIncluded = (course.included_tiers || []).includes(userTier);
  
  return (
    <div className="course-detail-page">
      {/* Hero */}
      <div className="course-hero">
        <button className="course-back" onClick={() => navigate('/courses')}>
          <ArrowLeft className="w-4 h-4" /> Back to Courses
        </button>
        
        <div className="course-hero-content">
          <div className="course-hero-info">
            <div className="course-hero-meta">
              {course.level && <span className="badge">{course.level}</span>}
              {course.rating_count > 0 && (
                <span className="course-hero-rating">
                  <Star className="w-4 h-4" /> {course.rating_avg?.toFixed(1)} ({course.rating_count} reviews)
                </span>
              )}
            </div>
            
            <h1>{course.title}</h1>
            <p className="course-hero-desc">{course.description}</p>
            
            <div className="course-hero-stats">
              <div><BookOpen className="w-4 h-4" /> {totalLessons} lessons</div>
              {course.total_duration_minutes > 0 && (
                <div><Clock className="w-4 h-4" /> {formatDuration(course.total_duration_minutes)}</div>
              )}
              {course.enrollment_count > 0 && (
                <div><Users className="w-4 h-4" /> {course.enrollment_count} enrolled</div>
              )}
            </div>
            
            {course.instructor_name && (
              <div className="course-instructor">
                {course.instructor_avatar_url && (
                  <img src={course.instructor_avatar_url} alt={course.instructor_name} />
                )}
                <div>
                  <div className="course-instructor-name">{course.instructor_name}</div>
                  {course.instructor_bio && <div className="course-instructor-bio">{course.instructor_bio}</div>}
                </div>
              </div>
            )}
          </div>
          
          <div className="course-hero-cta">
            {coverImageUrl && (
              <div className="course-hero-image">
                <img src={coverImageUrl} alt={course.title} />
                {course.promo_video_url && (
                  <button className="course-play-btn">
                    <PlayCircle className="w-12 h-12" />
                  </button>
                )}
              </div>
            )}
            
            <div className="course-hero-pricing">
              {isFree ? (
                <div className="course-price free">Free</div>
              ) : (
                <>
                  <div className="course-price">{formatPrice(course.price_cents)}</div>
                  {course.included_tiers?.length > 0 && (
                    <div className="course-included">
                      Included with {course.included_tiers.join(', ')}
                    </div>
                  )}
                </>
              )}
              
              {hasAccess ? (
                <button className="btn-primary w-full" onClick={() => navigate(`/courses/${courseId}/learn`)}>
                  <PlayCircle className="w-4 h-4" /> Continue Learning
                </button>
              ) : isFree || tierIncluded ? (
                <button className="btn-primary w-full" onClick={handleEnroll} disabled={enrolling}>
                  {enrolling ? 'Enrolling...' : 'Start Course'}
                </button>
              ) : (
                <button className="btn-primary w-full" onClick={handleEnroll} disabled={enrolling}>
                  {enrolling ? 'Processing...' : `Enroll for ${formatPrice(course.price_cents)}`}
                </button>
              )}
              
              <div className="course-guarantee">
                <Check className="w-4 h-4" /> Lifetime access
              </div>
              {checkoutStatus && (
                <div className="course-checkout-status">{checkoutStatus}</div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="course-detail-content">
        <div className="course-detail-main">
          {/* What you'll learn */}
          {course.what_youll_learn?.length > 0 && (
            <section className="course-section">
              <h2>What you'll learn</h2>
              <div className="course-learn-grid">
                {course.what_youll_learn.map((item, i) => (
                  <div key={i} className="course-learn-item">
                    <Check className="w-4 h-4" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* About this course */}
          {course.long_description && (
            <section className="course-section">
              <h2>About this course</h2>
              <div className="course-description">{course.long_description}</div>
            </section>
          )}
          
          {/* Curriculum */}
          <section className="course-section">
            <h2>Course Curriculum</h2>
            <div className="course-curriculum">
              {outline.length === 0 ? (
                <div className="course-curriculum-empty">Curriculum coming soon</div>
              ) : (
                outline.map((module, moduleIndex) => (
                  <div key={module.module_id} className="course-module">
                    <button 
                      className="course-module-header"
                      onClick={() => toggleModule(module.module_id)}
                    >
                      <div className="course-module-info">
                        <span className="course-module-num">Module {moduleIndex + 1}</span>
                        <span className="course-module-title">{module.title}</span>
                      </div>
                      <div className="course-module-meta">
                        <span>{module.lessons?.length || 0} lessons</span>
                        {expandedModules[module.module_id] ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </button>
                    
                    {expandedModules[module.module_id] && (
                      <div className="course-module-lessons">
                        {(module.lessons || []).map((lesson) => {
                          const signals = getLessonSignals(lesson);
                          return (
                          <div key={lesson.lesson_id} className="course-lesson-preview">
                            <div className="course-lesson-icon">
                              {lesson.is_preview ? (
                                <PlayCircle className="w-4 h-4" />
                              ) : hasAccess ? (
                                <Video className="w-4 h-4" />
                              ) : (
                                <Lock className="w-4 h-4" />
                              )}
                            </div>
                            <div className="course-lesson-info">
                              <div className="course-lesson-main">
                                <span className="course-lesson-title">{lesson.title}</span>
                                <div className="course-lesson-signals">
                                  <span className="course-lesson-chip">
                                    {signals.mediaLabel}
                                  </span>
                                  {signals.hasText && (
                                    <span className="course-lesson-chip">
                                      <FileText className="w-3 h-3" /> Notes
                                    </span>
                                  )}
                                  {signals.attachments > 0 && (
                                    <span className="course-lesson-chip">
                                      <Paperclip className="w-3 h-3" /> {signals.attachments}
                                    </span>
                                  )}
                                  {signals.resources > 0 && (
                                    <span className="course-lesson-chip">
                                      <Link2 className="w-3 h-3" /> {signals.resources}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="course-lesson-meta">
                                {lesson.duration_minutes > 0 && (
                                  <span className="course-lesson-duration">{lesson.duration_minutes}m</span>
                                )}
                                {lesson.is_preview && (
                                  <span className="course-lesson-preview-badge">Preview</span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                        })}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </section>
          
          {/* Requirements */}
          {course.requirements?.length > 0 && (
            <section className="course-section">
              <h2>Requirements</h2>
              <ul className="course-requirements">
                {course.requirements.map((req, i) => (
                  <li key={i}>{req}</li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== COURSE PLAYER ====================
function CoursePlayer() {
  const { courseId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [data, setData] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [lessonLoading, setLessonLoading] = useState(false);

  const loadLessonDetails = useCallback(async (lesson) => {
    if (!lesson?.lesson_id) {
      setCurrentLesson(null);
      return;
    }
    setCurrentLesson(lesson);
    setLessonLoading(true);
    try {
      const detail = await apiClient.get(`/api/courses/${courseId}/lessons/${lesson.lesson_id}`);
      setCurrentLesson({
        ...lesson,
        ...detail,
        progress: detail?.progress || lesson.progress
      });
    } catch (err) {
      console.error(err);
      setCurrentLesson(lesson);
    } finally {
      setLessonLoading(false);
    }
  }, [courseId]);
  
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const learnData = await apiClient.get(`/api/courses/${courseId}/learn`);
        setData(learnData);
        
        // Find current lesson
        const lessonId = searchParams.get('lesson');
        let found = null;
        
        if (lessonId) {
          for (const module of learnData.modules || []) {
            found = (module.lessons || []).find(l => l.lesson_id === lessonId);
            if (found) break;
          }
        }
        
        // Default to first incomplete or first lesson
        if (!found && learnData.modules?.length) {
          for (const module of learnData.modules) {
            for (const lesson of module.lessons || []) {
              if (!lesson.progress?.completed) {
                found = lesson;
                break;
              }
            }
            if (found) break;
          }
          if (!found) {
            found = learnData.modules[0]?.lessons?.[0];
          }
        }
        
        if (found) {
          await loadLessonDetails(found);
        } else {
          setCurrentLesson(null);
        }
      } catch (err) {
        console.error(err);
        navigate('/courses');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [courseId, searchParams, navigate, loadLessonDetails]);
  
  const selectLesson = (lesson) => {
    setSearchParams({ lesson: lesson.lesson_id });
    loadLessonDetails(lesson);
  };
  
  const markComplete = async () => {
    if (!currentLesson) return;
    try {
      await apiClient.post(`/api/courses/${courseId}/lessons/${currentLesson.lesson_id}/progress`, {
        completed: true
      });
      // Update local state
      setData(prev => {
        const updated = { ...prev };
        for (const module of updated.modules || []) {
          for (const lesson of module.lessons || []) {
            if (lesson.lesson_id === currentLesson.lesson_id) {
              lesson.progress = { ...lesson.progress, completed: true };
            }
          }
        }
        updated.progress = {
          ...updated.progress,
          completed_lessons: (updated.progress?.completed_lessons || 0) + 1,
          percent: Math.round(((updated.progress?.completed_lessons || 0) + 1) / (updated.progress?.total_lessons || 1) * 100)
        };
        return updated;
      });
      setCurrentLesson(prev => ({ ...prev, progress: { ...prev?.progress, completed: true } }));
    } catch (err) {
      console.error(err);
    }
  };
  
  const goToNextLesson = () => {
    if (!data?.modules || !currentLesson) return;
    let foundCurrent = false;
    for (const module of data.modules) {
      for (const lesson of module.lessons || []) {
        if (foundCurrent) {
          selectLesson(lesson);
          return;
        }
        if (lesson.lesson_id === currentLesson.lesson_id) {
          foundCurrent = true;
        }
      }
    }
  };
  
  if (loading) {
    return <div className="course-player"><div className="spinner" /></div>;
  }
  
  if (!data) {
    return (
      <div className="course-player">
        <div className="courses-empty">
          <h3>Unable to load course</h3>
          <button className="btn-secondary mt-4" onClick={() => navigate('/courses')}>Back to Courses</button>
        </div>
      </div>
    );
  }
  
  const { course, modules, progress } = data;
  const mediaType = normalizeMediaType(currentLesson || {});
  const mediaSignedUrl = cleanUrl(currentLesson?.media_signed_url || '');
  const resourceLinks = Array.isArray(currentLesson?.resource_links) ? currentLesson.resource_links : [];
  const attachments = Array.isArray(currentLesson?.attachments) ? currentLesson.attachments : [];
  const lessonSignals = getLessonSignals(currentLesson || {});
  const hasVideoUrlMedia = mediaType === 'video_url' && Boolean(cleanUrl(currentLesson?.video_url));
  const hasVideoAssetMedia = mediaType === 'video_asset' && Boolean(mediaSignedUrl);
  const hasImageUrlMedia = mediaType === 'image_url' && Boolean(cleanUrl(currentLesson?.image_url));
  const hasImageAssetMedia = mediaType === 'image_asset' && Boolean(mediaSignedUrl);
  const hasRenderableMedia = hasVideoUrlMedia || hasVideoAssetMedia || hasImageUrlMedia || hasImageAssetMedia;
  
  return (
    <div className="course-player">
      {/* Header */}
      <div className="course-player-header">
        <button className="course-player-back" onClick={() => navigate(`/courses/${courseId}`)}>
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="course-player-title">{course?.title}</div>
        <div className="course-player-progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress?.percent || 0}%` }} />
          </div>
          <span>{progress?.percent || 0}% complete</span>
        </div>
        <button 
          className="course-player-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          <Layers className="w-4 h-4" />
        </button>
      </div>
      
      <div className="course-player-body">
        {/* Main content */}
        <div className="course-player-main">
          {currentLesson ? (
            <>
              {lessonLoading ? (
                <div className="course-lesson-loading-inline">
                  <div className="spinner" />
                  <p>Loading lesson...</p>
                </div>
              ) : null}

              {!lessonLoading && hasVideoUrlMedia ? (
                isVideoEmbedUrl(currentLesson.video_url) ? (
                  <div className="course-video-container">
                    <iframe
                      src={toEmbedUrl(currentLesson.video_url)}
                      title={currentLesson.title}
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <div className="course-video-container">
                    <video
                      src={cleanUrl(currentLesson.video_url)}
                      controls
                      controlsList="nodownload noplaybackrate"
                      onContextMenu={(event) => event.preventDefault()}
                    />
                  </div>
                )
              ) : mediaType === 'video_asset' && mediaSignedUrl ? (
                <div className="course-video-container">
                  <video
                    src={mediaSignedUrl}
                    controls
                    controlsList="nodownload noplaybackrate"
                    onContextMenu={(event) => event.preventDefault()}
                  />
                </div>
              ) : mediaType === 'image_url' && cleanUrl(currentLesson.image_url) ? (
                <div className="course-image-container">
                  <img src={cleanUrl(currentLesson.image_url)} alt={currentLesson.title} />
                </div>
              ) : mediaType === 'image_asset' && mediaSignedUrl ? (
                <div className="course-image-container">
                  <img src={mediaSignedUrl} alt={currentLesson.title} />
                </div>
              ) : null}
              
              <div className="course-lesson-content">
                <h2>{currentLesson.title}</h2>
                <div className="course-lesson-header-meta">
                  <span className="course-lesson-chip">
                    {lessonSignals.mediaLabel}
                  </span>
                  {lessonSignals.hasText && (
                    <span className="course-lesson-chip">
                      <FileText className="w-3 h-3" /> Notes
                    </span>
                  )}
                  {lessonSignals.attachments > 0 && (
                    <span className="course-lesson-chip">
                      <Paperclip className="w-3 h-3" /> {lessonSignals.attachments} files
                    </span>
                  )}
                  {lessonSignals.resources > 0 && (
                    <span className="course-lesson-chip">
                      <Link2 className="w-3 h-3" /> {lessonSignals.resources} links
                    </span>
                  )}
                  {currentLesson.is_preview && (
                    <span className="course-lesson-chip preview">Preview lesson</span>
                  )}
                </div>
                {currentLesson.description && (
                  <p className="course-lesson-desc">{currentLesson.description}</p>
                )}
                {!lessonLoading && !hasRenderableMedia && (
                  <div className="course-lesson-no-media">No media attached to this lesson.</div>
                )}
                {currentLesson.content_md && (
                  <div className="course-lesson-text">{linkifyText(currentLesson.content_md)}</div>
                )}

                {(attachments.length > 0 || resourceLinks.length > 0) && (
                  <div className="course-lesson-resource-grid">
                    {attachments.length > 0 && (
                      <section className="course-lesson-resource-card">
                        <h3><Paperclip className="w-4 h-4" /> Downloads</h3>
                        <div className="course-lesson-resource-list">
                          {attachments.map((attachment) => {
                            const href = cleanUrl(attachment.download_url || attachment.url || '');
                            return (
                              <a
                                key={attachment.id || attachment.path}
                                href={href || '#'}
                                className={`course-lesson-resource-item ${href ? '' : 'disabled'}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(event) => {
                                  if (!href) event.preventDefault();
                                }}
                              >
                                <span className="resource-main">
                                  <Paperclip className="w-4 h-4" />
                                  <span>{attachment.name || attachment.path}</span>
                                </span>
                                <span className="resource-meta">
                                  {formatFileSize(attachment.size_bytes)}
                                  <ExternalLink className="w-3 h-3" />
                                </span>
                              </a>
                            );
                          })}
                        </div>
                      </section>
                    )}

                    {resourceLinks.length > 0 && (
                      <section className="course-lesson-resource-card">
                        <h3><Link2 className="w-4 h-4" /> Resources</h3>
                        <div className="course-lesson-resource-list">
                          {resourceLinks.map((resource) => {
                            const href = getResourceHref(resource);
                            const isExternal = href.startsWith('http://') || href.startsWith('https://');
                            return (
                              <a
                                key={resource.id || `${resource.resource_type}-${resource.resource_id}-${resource.label}`}
                                href={href || '#'}
                                className={`course-lesson-resource-item ${href ? '' : 'disabled'}`}
                                target={isExternal ? '_blank' : undefined}
                                rel={isExternal ? 'noopener noreferrer' : undefined}
                                onClick={(event) => {
                                  if (!href) event.preventDefault();
                                }}
                              >
                                <span className="resource-main">
                                  <Link2 className="w-4 h-4" />
                                  <span>{resource.label || href || 'Resource'}</span>
                                </span>
                                <span className="resource-meta">
                                  {resource.resource_type || 'external'}
                                  <ExternalLink className="w-3 h-3" />
                                </span>
                              </a>
                            );
                          })}
                        </div>
                      </section>
                    )}
                  </div>
                )}
                
                <div className="course-lesson-actions">
                  {!currentLesson.progress?.completed && (
                    <button className="btn-primary" onClick={markComplete}>
                      <CheckCircle2 className="w-4 h-4" /> Mark as Complete
                    </button>
                  )}
                  <button className="btn-secondary" onClick={goToNextLesson}>
                    Next Lesson <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="course-video-placeholder">
              <BookOpen className="w-16 h-16" />
              <p>Select a lesson to start</p>
            </div>
          )}
        </div>
        
        {/* Sidebar */}
        <div className={`course-player-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="course-sidebar-header">
            <h3>Course Content</h3>
            <span>{progress?.completed_lessons || 0} / {progress?.total_lessons || 0}</span>
          </div>
          
          <div className="course-sidebar-modules">
            {(modules || []).map((module, moduleIndex) => (
              <div key={module.module_id} className="course-sidebar-module">
                <div className="course-sidebar-module-header">
                  <span className="module-num">{moduleIndex + 1}</span>
                  <span className="module-title">{module.title}</span>
                </div>
                <div className="course-sidebar-lessons">
                  {(module.lessons || []).map((lesson) => {
                    const signals = getLessonSignals(lesson);
                    return (
                    <button
                      key={lesson.lesson_id}
                      className={`course-sidebar-lesson ${currentLesson?.lesson_id === lesson.lesson_id ? 'active' : ''} ${lesson.progress?.completed ? 'completed' : ''}`}
                      onClick={() => selectLesson(lesson)}
                    >
                      {lesson.progress?.completed ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      ) : (
                        <Circle className="w-4 h-4" />
                      )}
                      <span className="lesson-main">
                        <span className="lesson-title">{lesson.title}</span>
                        <span className="lesson-flags">
                          {signals.hasText && <span className="lesson-flag"><FileText className="w-3 h-3" /></span>}
                          {signals.attachments > 0 && <span className="lesson-flag"><Paperclip className="w-3 h-3" />{signals.attachments}</span>}
                          {signals.resources > 0 && <span className="lesson-flag"><Link2 className="w-3 h-3" />{signals.resources}</span>}
                          {lesson.is_preview && <span className="lesson-flag preview">Preview</span>}
                        </span>
                      </span>
                      <span className="lesson-duration">
                        {lesson.duration_minutes > 0 ? `${lesson.duration_minutes}m` : ''}
                      </span>
                    </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== COURSE BUILDER (ADMIN) ====================
function CourseBuilder() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = React.useContext(AuthContext);
  const isNew = courseId === 'new';
  
  const isAdmin = Boolean(
    user?.permissions?.courses_manage ||
    user?.is_admin ||
    user?.account_level === 'admin'
  );
  
  const [activeTab, setActiveTab] = useState('details');
  const [course, setCourse] = useState({
    title: '',
    description: '',
    long_description: '',
    price_cents: 0,
    currency: 'USD',
    is_published: false,
    level: '',
    cover_image_url: '',
    promo_video_url: '',
    tags: [],
    included_tiers: [],
    instructor_name: '',
    instructor_bio: '',
    instructor_avatar_url: '',
    what_youll_learn: [],
    requirements: []
  });
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploadingKey, setUploadingKey] = useState('');
  const [editingModuleIds, setEditingModuleIds] = useState({});
  const [editingLessonIds, setEditingLessonIds] = useState({});
  const [savingModuleIds, setSavingModuleIds] = useState({});
  const [savingLessonIds, setSavingLessonIds] = useState({});
  const [expandedLessonIds, setExpandedLessonIds] = useState({});
  const [dragState, setDragState] = useState(null);
  
  // Temp inputs
  const [newLearnItem, setNewLearnItem] = useState('');
  const [newRequirement, setNewRequirement] = useState('');
  const [newTag, setNewTag] = useState('');
  
  const loadCourse = useCallback(async () => {
    setLoading(true);
    try {
      const [courseData, outlineData] = await Promise.all([
        apiClient.get(`/api/courses/${courseId}`),
        apiClient.get(`/api/courses/${courseId}/outline`)
      ]);
      setCourse({
        ...courseData,
        title: courseData.title || '',
        description: courseData.description || '',
        long_description: courseData.long_description || '',
        cover_image_url: courseData.cover_image_url || '',
        promo_video_url: courseData.promo_video_url || '',
        instructor_name: courseData.instructor_name || '',
        instructor_bio: courseData.instructor_bio || '',
        instructor_avatar_url: courseData.instructor_avatar_url || '',
        tags: courseData.tags || [],
        included_tiers: courseData.included_tiers || [],
        what_youll_learn: courseData.what_youll_learn || [],
        requirements: courseData.requirements || []
      });
      setModules((outlineData?.modules || []).map((module) => ({
        ...module,
        lessons: (module.lessons || []).map((lesson) => ({
          ...lesson,
          attachments: Array.isArray(lesson.attachments) ? lesson.attachments : [],
          resource_links: Array.isArray(lesson.resource_links) ? lesson.resource_links : [],
          media_type: normalizeMediaType(lesson)
        }))
      })));
      setEditingModuleIds({});
      setEditingLessonIds({});
      setSavingModuleIds({});
      setSavingLessonIds({});
      setExpandedLessonIds({});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/courses');
      return;
    }
    if (!isNew) {
      loadCourse();
    }
  }, [isNew, isAdmin, navigate, loadCourse]);
  
  const handleSave = async () => {
    if (!course.title.trim()) {
      setError('Course title is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...course,
        price_cents: course.price_cents || 0
      };
      
      if (isNew) {
        const created = await apiClient.post('/api/courses', payload);
        navigate(`/courses/${created.course_id}/builder`, { replace: true });
      } else {
        await saveAllEditedItems();
        await apiClient.patch(`/api/courses/${courseId}`, payload);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const getLessonById = (lessonId) => {
    for (const module of modules) {
      const found = (module.lessons || []).find((lesson) => lesson.lesson_id === lessonId);
      if (found) return found;
    }
    return null;
  };

  const updateLessonLocal = (lessonId, updates) => {
    setModules(prev => prev.map(module => ({
      ...module,
      lessons: (module.lessons || []).map(lesson =>
        lesson.lesson_id === lessonId ? { ...lesson, ...updates } : lesson
      )
    })));
  };

  const updateModuleLocal = (moduleId, updates) => {
    setModules(prev => prev.map(module =>
      module.module_id === moduleId ? { ...module, ...updates } : module
    ));
  };

  const getModuleById = (moduleId) => modules.find((module) => module.module_id === moduleId) || null;

  const buildModulePayload = (module) => ({
    title: module?.title || '',
    description: module?.description || '',
    order_index: module?.order_index || 0
  });

  const buildLessonPayload = (lesson) => ({
    title: lesson?.title || '',
    description: lesson?.description || '',
    video_url: lesson?.video_url || '',
    image_url: lesson?.image_url || '',
    content_md: lesson?.content_md || '',
    duration_minutes: parseInt(lesson?.duration_minutes, 10) || 0,
    is_preview: !!lesson?.is_preview,
    media_type: normalizeMediaType(lesson),
    media_bucket: lesson?.media_bucket || '',
    media_path: lesson?.media_path || '',
    media_filename: lesson?.media_filename || '',
    media_content_type: lesson?.media_content_type || '',
    attachments: Array.isArray(lesson?.attachments) ? lesson.attachments : [],
    resource_links: Array.isArray(lesson?.resource_links) ? lesson.resource_links : [],
    order_index: lesson?.order_index || 0
  });

  const requestLessonUploadIntent = async (lessonId, kind, file) => {
    return apiClient.post(`/api/courses/lessons/${lessonId}/upload-intent`, {
      kind,
      filename: file.name,
      content_type: file.type || 'application/octet-stream'
    });
  };

  const uploadFileToSignedUrl = async (intent, file) => {
    const uploadUrl = cleanUrl(intent?.upload_url || intent?.url || '');
    if (!uploadUrl) {
      throw new Error('Upload URL was not returned by the server');
    }
    const headerEntries = Object.entries(intent?.headers || {}).filter(([, value]) => value !== undefined && value !== null && value !== '');
    const headers = headerEntries.reduce((acc, [key, value]) => ({ ...acc, [key]: String(value) }), {});
    if (!headers['content-type'] && file.type) {
      headers['content-type'] = file.type;
    }
    const response = await fetch(uploadUrl, {
      method: String(intent?.method || 'PUT').toUpperCase(),
      headers,
      body: file
    });
    if (!response.ok) {
      throw new Error(`Upload failed (${response.status})`);
    }
  };

  const handleUploadLessonMedia = async (lesson, type, file) => {
    if (!lesson?.lesson_id || !file) return;
    const kind = type === 'video' ? 'lesson_video' : 'lesson_image';
    setError('');
    setUploadingKey(`${lesson.lesson_id}:${kind}`);
    try {
      const intent = await requestLessonUploadIntent(lesson.lesson_id, kind, file);
      await uploadFileToSignedUrl(intent, file);
      const mediaType = type === 'video' ? 'video_asset' : 'image_asset';
      updateLessonLocal(lesson.lesson_id, {
        media_type: mediaType,
        media_bucket: intent.bucket,
        media_path: intent.path,
        media_filename: file.name,
        media_content_type: file.type || 'application/octet-stream',
        ...(type === 'video' ? { video_url: '' } : { image_url: '' })
      });
    } catch (err) {
      setError(err.message || 'Unable to upload file');
    } finally {
      setUploadingKey('');
    }
  };

  const handleUploadLessonAttachment = async (lesson, file) => {
    if (!lesson?.lesson_id || !file) return;
    setError('');
    setUploadingKey(`${lesson.lesson_id}:attachment`);
    try {
      const intent = await requestLessonUploadIntent(lesson.lesson_id, 'attachment', file);
      await uploadFileToSignedUrl(intent, file);
      const current = getLessonById(lesson.lesson_id);
      const attachments = Array.isArray(current?.attachments) ? current.attachments : [];
      const nextAttachments = [
        ...attachments,
        {
          id: makeClientId(),
          name: file.name,
          bucket: intent.bucket,
          path: intent.path,
          content_type: file.type || 'application/octet-stream',
          size_bytes: file.size || 0
        }
      ];
      updateLessonLocal(lesson.lesson_id, { attachments: nextAttachments });
    } catch (err) {
      setError(err.message || 'Unable to upload attachment');
    } finally {
      setUploadingKey('');
    }
  };

  const removeLessonAttachment = (lessonId, attachmentId) => {
    const lesson = getLessonById(lessonId);
    const attachments = Array.isArray(lesson?.attachments) ? lesson.attachments : [];
    const nextAttachments = attachments.filter((item) => (item.id || item.path) !== attachmentId);
    updateLessonLocal(lessonId, { attachments: nextAttachments });
  };

  const addResourceLink = (lessonId) => {
    const lesson = getLessonById(lessonId);
    const links = Array.isArray(lesson?.resource_links) ? lesson.resource_links : [];
    const nextLinks = [
      ...links,
      {
        id: makeClientId(),
        label: '',
        resource_type: 'external',
        resource_id: '',
        url: '',
        project_id: '',
        open_in_new_tab: true
      }
    ];
    updateLessonLocal(lessonId, { resource_links: nextLinks });
  };

  const stageResourceLink = (lessonId, linkId, updates) => {
    const lesson = getLessonById(lessonId);
    const links = Array.isArray(lesson?.resource_links) ? lesson.resource_links : [];
    const nextLinks = links.map((item) => {
      const itemId = item.id || `${item.resource_type}-${item.resource_id}-${item.label}`;
      if (itemId !== linkId) return item;
      const nextItem = { ...item, ...updates };
      if (nextItem.resource_type === 'external') {
        nextItem.resource_id = '';
        nextItem.project_id = '';
      }
      return nextItem;
    });
    updateLessonLocal(lessonId, { resource_links: nextLinks });
  };

  const removeResourceLink = (lessonId, linkId) => {
    const lesson = getLessonById(lessonId);
    const links = Array.isArray(lesson?.resource_links) ? lesson.resource_links : [];
    const nextLinks = links.filter((item) => {
      const itemId = item.id || `${item.resource_type}-${item.resource_id}-${item.label}`;
      return itemId !== linkId;
    });
    updateLessonLocal(lessonId, { resource_links: nextLinks });
  };

  const startModuleEditing = (moduleId) => {
    setEditingModuleIds(prev => ({ ...prev, [moduleId]: true }));
  };

  const saveModuleEditing = async (moduleId) => {
    const module = getModuleById(moduleId);
    if (!module) return;
    const payload = buildModulePayload(module);
    if (!payload.title.trim()) {
      setError('Module title is required');
      return;
    }
    setSavingModuleIds(prev => ({ ...prev, [moduleId]: true }));
    try {
      await updateModule(moduleId, payload);
      setEditingModuleIds(prev => ({ ...prev, [moduleId]: false }));
    } catch (err) {
      setError(err.message || 'Unable to save module');
    } finally {
      setSavingModuleIds(prev => ({ ...prev, [moduleId]: false }));
    }
  };

  const startLessonEditing = (lessonId) => {
    setEditingLessonIds(prev => ({ ...prev, [lessonId]: true }));
    setExpandedLessonIds(prev => ({ ...prev, [lessonId]: true }));
  };

  const saveLessonEditing = async (lessonId) => {
    const lesson = getLessonById(lessonId);
    if (!lesson) return;
    const payload = buildLessonPayload(lesson);
    if (!payload.title.trim()) {
      setError('Lesson title is required');
      return;
    }
    setSavingLessonIds(prev => ({ ...prev, [lessonId]: true }));
    try {
      await updateLesson(lessonId, payload);
      setEditingLessonIds(prev => ({ ...prev, [lessonId]: false }));
    } catch (err) {
      setError(err.message || 'Unable to save lesson');
    } finally {
      setSavingLessonIds(prev => ({ ...prev, [lessonId]: false }));
    }
  };

  const saveAllEditedItems = async () => {
    const moduleIds = Object.keys(editingModuleIds).filter((id) => editingModuleIds[id]);
    const lessonIds = Object.keys(editingLessonIds).filter((id) => editingLessonIds[id]);
    for (const moduleId of moduleIds) {
      await saveModuleEditing(moduleId);
    }
    for (const lessonId of lessonIds) {
      await saveLessonEditing(lessonId);
    }
  };

  const toggleLessonExpanded = (lessonId) => {
    setExpandedLessonIds(prev => ({ ...prev, [lessonId]: !prev[lessonId] }));
  };

  const reorderItems = (items, sourceIndex, targetIndex) => {
    const next = [...items];
    const [moved] = next.splice(sourceIndex, 1);
    next.splice(targetIndex, 0, moved);
    return next;
  };

  const persistModuleOrder = async (orderedModules) => {
    await Promise.all(
      orderedModules.map((module, index) =>
        apiClient.patch(`/api/courses/modules/${module.module_id}`, { order_index: index })
      )
    );
  };

  const persistLessonOrder = async (lessons) => {
    await Promise.all(
      lessons.map((lesson, index) =>
        apiClient.patch(`/api/courses/lessons/${lesson.lesson_id}`, { order_index: index })
      )
    );
  };

  const handleModuleDragStart = (event, moduleId) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', moduleId);
    setDragState({ type: 'module', moduleId });
  };

  const handleModuleDrop = async (event, targetModuleId) => {
    event.preventDefault();
    const active = dragState;
    setDragState(null);
    if (!active || active.type !== 'module' || active.moduleId === targetModuleId) return;

    const sourceIndex = modules.findIndex((module) => module.module_id === active.moduleId);
    const targetIndex = modules.findIndex((module) => module.module_id === targetModuleId);
    if (sourceIndex < 0 || targetIndex < 0) return;

    const reordered = reorderItems(modules, sourceIndex, targetIndex).map((module, index) => ({
      ...module,
      order_index: index
    }));

    setModules(reordered);
    try {
      await persistModuleOrder(reordered);
    } catch (err) {
      setError(err.message || 'Unable to reorder modules');
      await loadCourse();
    }
  };

  const handleLessonDragStart = (event, moduleId, lessonId) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', lessonId);
    setDragState({ type: 'lesson', moduleId, lessonId });
  };

  const handleLessonDrop = async (event, moduleId, targetLessonId) => {
    event.preventDefault();
    const active = dragState;
    setDragState(null);
    if (!active || active.type !== 'lesson' || active.moduleId !== moduleId || active.lessonId === targetLessonId) return;

    const module = modules.find((item) => item.module_id === moduleId);
    const lessons = module?.lessons || [];
    const sourceIndex = lessons.findIndex((lesson) => lesson.lesson_id === active.lessonId);
    const targetIndex = lessons.findIndex((lesson) => lesson.lesson_id === targetLessonId);
    if (sourceIndex < 0 || targetIndex < 0) return;

    const reorderedLessons = reorderItems(lessons, sourceIndex, targetIndex).map((lesson, index) => ({
      ...lesson,
      order_index: index
    }));

    setModules((prev) => prev.map((item) => (
      item.module_id === moduleId
        ? { ...item, lessons: reorderedLessons }
        : item
    )));

    try {
      await persistLessonOrder(reorderedLessons);
    } catch (err) {
      setError(err.message || 'Unable to reorder lessons');
      await loadCourse();
    }
  };

  const handleLessonDropToEnd = async (event, moduleId) => {
    event.preventDefault();
    const active = dragState;
    setDragState(null);
    if (!active || active.type !== 'lesson' || active.moduleId !== moduleId) return;

    const module = modules.find((item) => item.module_id === moduleId);
    const lessons = module?.lessons || [];
    const sourceIndex = lessons.findIndex((lesson) => lesson.lesson_id === active.lessonId);
    if (sourceIndex < 0 || sourceIndex === lessons.length - 1) return;

    const reorderedLessons = reorderItems(lessons, sourceIndex, lessons.length - 1).map((lesson, index) => ({
      ...lesson,
      order_index: index
    }));

    setModules((prev) => prev.map((item) => (
      item.module_id === moduleId
        ? { ...item, lessons: reorderedLessons }
        : item
    )));

    try {
      await persistLessonOrder(reorderedLessons);
    } catch (err) {
      setError(err.message || 'Unable to reorder lessons');
      await loadCourse();
    }
  };
  
  // Module management
  const addModule = async () => {
    if (isNew) {
      setError('Save the course first to add modules');
      return;
    }
    try {
      const created = await apiClient.post(`/api/courses/${courseId}/modules`, {
        title: 'New Module',
        description: '',
        order_index: modules.length
      });
      setModules(prev => [...prev, { ...created, lessons: [] }]);
      setEditingModuleIds(prev => ({ ...prev, [created.module_id]: true }));
    } catch (err) {
      setError(err.message);
    }
  };
  
  const updateModule = async (moduleId, updates) => {
    try {
      await apiClient.patch(`/api/courses/modules/${moduleId}`, updates);
      setModules(prev => prev.map(m => m.module_id === moduleId ? { ...m, ...updates } : m));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };
  
  const deleteModule = async (moduleId) => {
    if (!window.confirm('Delete this module and all its lessons?')) return;
    try {
      await apiClient.del(`/api/courses/modules/${moduleId}`);
      setModules(prev => prev.filter(m => m.module_id !== moduleId));
      setEditingModuleIds(prev => {
        const next = { ...prev };
        delete next[moduleId];
        return next;
      });
    } catch (err) {
      setError(err.message);
    }
  };
  
  // Lesson management
  const addLesson = async (moduleId) => {
    try {
      const module = modules.find(m => m.module_id === moduleId);
      const created = await apiClient.post(`/api/courses/modules/${moduleId}/lessons`, {
        title: 'New Lesson',
        description: '',
        video_url: '',
        image_url: '',
        content_md: '',
        duration_minutes: 0,
        is_preview: false,
        media_type: 'video_url',
        attachments: [],
        resource_links: [],
        order_index: (module?.lessons?.length || 0)
      });
      setModules(prev => prev.map(m => 
        m.module_id === moduleId 
          ? { ...m, lessons: [...(m.lessons || []), created] }
          : m
      ));
      setEditingLessonIds(prev => ({ ...prev, [created.lesson_id]: true }));
      setExpandedLessonIds(prev => ({ ...prev, [created.lesson_id]: true }));
    } catch (err) {
      setError(err.message);
    }
  };
  
  const updateLesson = async (lessonId, updates) => {
    try {
      await apiClient.patch(`/api/courses/lessons/${lessonId}`, updates);
      setModules(prev => prev.map(m => ({
        ...m,
        lessons: (m.lessons || []).map(l => l.lesson_id === lessonId ? { ...l, ...updates } : l)
      })));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };
  
  const deleteLesson = async (lessonId) => {
    try {
      await apiClient.del(`/api/courses/lessons/${lessonId}`);
      setModules(prev => prev.map(m => ({
        ...m,
        lessons: (m.lessons || []).filter(l => l.lesson_id !== lessonId)
      })));
      setEditingLessonIds(prev => {
        const next = { ...prev };
        delete next[lessonId];
        return next;
      });
      setExpandedLessonIds(prev => {
        const next = { ...prev };
        delete next[lessonId];
        return next;
      });
    } catch (err) {
      setError(err.message);
    }
  };
  
  if (!isAdmin) return null;
  
  if (loading) {
    return <div className="course-builder"><div className="spinner" /></div>;
  }
  
  return (
    <div className="course-builder">
      <div className="course-builder-header">
        <div className="course-builder-header-main">
          <button className="course-builder-back" onClick={() => navigate('/courses')}>
            <ArrowLeft className="w-4 h-4" /> Courses
          </button>
          <div className="course-builder-title-wrap">
            <h1>{isNew ? 'Create Course' : 'Edit Course'}</h1>
            <p>{isNew ? 'Set up your course details first, then build curriculum.' : 'Refine content, curriculum, and pricing in one workspace.'}</p>
          </div>
        </div>
        <div className="course-builder-top-actions">
          {!isNew && (
            <button className="btn-secondary course-builder-header-btn" onClick={() => navigate(`/courses/${courseId}`)}>
              <Eye className="w-4 h-4" /> Preview
            </button>
          )}
          <button className="btn-primary course-builder-header-btn" onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
      
      {error && <div className="error-banner">{error}</div>}
      
      <div className="course-builder-tabs-wrap">
        <div className="course-builder-tabs">
          <button className={activeTab === 'details' ? 'active' : ''} onClick={() => setActiveTab('details')}>
            <Settings className="w-4 h-4" /> Details
          </button>
          <button 
            className={activeTab === 'curriculum' ? 'active' : ''} 
            onClick={() => setActiveTab('curriculum')}
            disabled={isNew}
          >
            <Layers className="w-4 h-4" /> Curriculum
          </button>
          <button 
            className={activeTab === 'pricing' ? 'active' : ''} 
            onClick={() => setActiveTab('pricing')}
          >
            <BarChart3 className="w-4 h-4" /> Pricing & Access
          </button>
        </div>
      </div>
      
      <div className="course-builder-content">
        {/* Details Tab */}
        {activeTab === 'details' && (
          <div className="course-builder-form course-builder-form--details">
            <div className="form-section">
              <h3>Basic Information</h3>
              
              <div className="form-field">
                <label>Course Title *</label>
                <input
                  className="input-glass"
                  value={course.title}
                  onChange={(e) => setCourse(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Mastering AI Prompts"
                />
              </div>
              
              <div className="form-field">
                <label>Short Description</label>
                <textarea
                  className="input-glass"
                  rows="2"
                  value={course.description}
                  onChange={(e) => setCourse(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="A brief description shown in course cards"
                />
              </div>
              
              <div className="form-field">
                <label>Full Description</label>
                <textarea
                  className="input-glass"
                  rows="6"
                  value={course.long_description}
                  onChange={(e) => setCourse(prev => ({ ...prev, long_description: e.target.value }))}
                  placeholder="Detailed course description"
                />
              </div>
              
              <div className="form-row">
                <div className="form-field">
                  <label>Level</label>
                  <SelectMenu
                    value={course.level}
                    options={LEVEL_OPTIONS}
                    placeholder="Select level"
                    onChange={(v) => setCourse(prev => ({ ...prev, level: v }))}
                  />
                </div>
                <div className="form-field">
                  <label>Status</label>
                  <SelectMenu
                    value={course.is_published ? 'published' : 'draft'}
                    options={[
                      { value: 'draft', label: 'Draft' },
                      { value: 'published', label: 'Published' }
                    ]}
                    onChange={(v) => setCourse(prev => ({ ...prev, is_published: v === 'published' }))}
                  />
                </div>
              </div>
            </div>
            
            <div className="form-section">
              <h3>Media</h3>
              
              <div className="form-field">
                <label>Cover Image URL</label>
                <input
                  className="input-glass"
                  value={course.cover_image_url}
                  onChange={(e) => setCourse(prev => ({ ...prev, cover_image_url: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
              
              <div className="form-field">
                <label>Promo Video URL</label>
                <input
                  className="input-glass"
                  value={course.promo_video_url}
                  onChange={(e) => setCourse(prev => ({ ...prev, promo_video_url: e.target.value }))}
                  placeholder="YouTube or Vimeo URL"
                />
              </div>
            </div>
            
            <div className="form-section">
              <h3>Instructor</h3>
              
              <div className="form-row">
                <div className="form-field">
                  <label>Instructor Name</label>
                  <input
                    className="input-glass"
                    value={course.instructor_name}
                    onChange={(e) => setCourse(prev => ({ ...prev, instructor_name: e.target.value }))}
                  />
                </div>
                <div className="form-field">
                  <label>Instructor Avatar URL</label>
                  <input
                    className="input-glass"
                    value={course.instructor_avatar_url}
                    onChange={(e) => setCourse(prev => ({ ...prev, instructor_avatar_url: e.target.value }))}
                  />
                </div>
              </div>
              
              <div className="form-field">
                <label>Instructor Bio</label>
                <textarea
                  className="input-glass"
                  rows="3"
                  value={course.instructor_bio}
                  onChange={(e) => setCourse(prev => ({ ...prev, instructor_bio: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="form-section form-section--wide">
              <h3>What Students Will Learn</h3>
              <div className="form-list">
                {(course.what_youll_learn || []).map((item, i) => (
                  <div key={i} className="form-list-item">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>{item}</span>
                    <button onClick={() => setCourse(prev => ({
                      ...prev,
                      what_youll_learn: prev.what_youll_learn.filter((_, idx) => idx !== i)
                    }))}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <div className="form-list-add">
                  <input
                    className="input-glass"
                    value={newLearnItem}
                    onChange={(e) => setNewLearnItem(e.target.value)}
                    placeholder="Add learning outcome"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newLearnItem.trim()) {
                        setCourse(prev => ({
                          ...prev,
                          what_youll_learn: [...(prev.what_youll_learn || []), newLearnItem.trim()]
                        }));
                        setNewLearnItem('');
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="btn-secondary btn-compact"
                    onClick={() => {
                      if (newLearnItem.trim()) {
                        setCourse(prev => ({
                          ...prev,
                          what_youll_learn: [...(prev.what_youll_learn || []), newLearnItem.trim()]
                        }));
                        setNewLearnItem('');
                      }
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
            
            <div className="form-section form-section--wide">
              <h3>Requirements</h3>
              <div className="form-list">
                {(course.requirements || []).map((item, i) => (
                  <div key={i} className="form-list-item">
                    <span>{item}</span>
                    <button onClick={() => setCourse(prev => ({
                      ...prev,
                      requirements: prev.requirements.filter((_, idx) => idx !== i)
                    }))}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <div className="form-list-add">
                  <input
                    className="input-glass"
                    value={newRequirement}
                    onChange={(e) => setNewRequirement(e.target.value)}
                    placeholder="Add requirement"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newRequirement.trim()) {
                        setCourse(prev => ({
                          ...prev,
                          requirements: [...(prev.requirements || []), newRequirement.trim()]
                        }));
                        setNewRequirement('');
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="btn-secondary btn-compact"
                    onClick={() => {
                      if (newRequirement.trim()) {
                        setCourse(prev => ({
                          ...prev,
                          requirements: [...(prev.requirements || []), newRequirement.trim()]
                        }));
                        setNewRequirement('');
                      }
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Curriculum Tab */}
        {activeTab === 'curriculum' && (
          <div className="course-curriculum-builder">
            <div className="curriculum-header">
              <h3>Course Modules</h3>
              <button className="btn-primary btn-compact curriculum-add-btn" onClick={addModule}>
                <Plus className="w-4 h-4" /> Add Module
              </button>
            </div>
            
            {modules.length === 0 ? (
              <div className="curriculum-empty">
                <Layers className="w-12 h-12" />
                <h4>No modules yet</h4>
                <p>Add your first module to start building the curriculum</p>
              </div>
            ) : (
              <div className="curriculum-modules">
                {modules.map((module, moduleIndex) => {
                  const moduleIsEditing = !!editingModuleIds[module.module_id];
                  const moduleIsSaving = !!savingModuleIds[module.module_id];
                  return (
                  <div
                    key={module.module_id}
                    className={`curriculum-module ${dragState?.type === 'module' && dragState?.moduleId === module.module_id ? 'is-dragging' : ''}`}
                    onDragOver={(event) => {
                      if (dragState?.type === 'module') event.preventDefault();
                    }}
                    onDrop={(event) => handleModuleDrop(event, module.module_id)}
                  >
                    <div className="curriculum-module-header">
                      <button
                        type="button"
                        className="curriculum-module-drag"
                        draggable
                        onDragStart={(event) => handleModuleDragStart(event, module.module_id)}
                        onDragEnd={() => setDragState(null)}
                        aria-label={`Drag ${module.title || `module ${moduleIndex + 1}`}`}
                        title="Drag to reorder module"
                      >
                        <GripVertical className="w-4 h-4" />
                      </button>
                      <span className="curriculum-module-pill">Module {moduleIndex + 1}</span>
                      <input
                        className="curriculum-module-title"
                        value={module.title}
                        onChange={(e) => updateModuleLocal(module.module_id, { title: e.target.value })}
                        placeholder="Module title"
                        disabled={!moduleIsEditing}
                      />
                      <div className="curriculum-module-actions">
                        {moduleIsEditing ? (
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => saveModuleEditing(module.module_id)}
                            disabled={moduleIsSaving}
                          >
                            <Save className="w-4 h-4" /> {moduleIsSaving ? 'Saving...' : 'Save'}
                          </button>
                        ) : (
                          <button type="button" onClick={() => startModuleEditing(module.module_id)}>
                            <Pencil className="w-4 h-4" /> Edit
                          </button>
                        )}
                        <button type="button" onClick={() => addLesson(module.module_id)}>
                          <Plus className="w-4 h-4" /> Lesson
                        </button>
                        <button type="button" className="danger" onClick={() => deleteModule(module.module_id)}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    <div
                      className="curriculum-lessons"
                      onDragOver={(event) => {
                        if (dragState?.type === 'lesson' && dragState?.moduleId === module.module_id) {
                          event.preventDefault();
                        }
                      }}
                      onDrop={(event) => handleLessonDropToEnd(event, module.module_id)}
                    >
                      {(module.lessons || []).map((lesson) => {
                        const lessonIsEditing = !!editingLessonIds[lesson.lesson_id];
                        const lessonIsSaving = !!savingLessonIds[lesson.lesson_id];
                        const lessonIsExpanded = !!expandedLessonIds[lesson.lesson_id];
                        const lessonModel = lesson;
                        return (
                        <div
                          key={lesson.lesson_id}
                          className={`curriculum-lesson ${dragState?.type === 'lesson' && dragState?.lessonId === lesson.lesson_id ? 'is-dragging' : ''}`}
                          onDragOver={(event) => {
                            if (dragState?.type === 'lesson' && dragState?.moduleId === module.module_id) {
                              event.preventDefault();
                            }
                          }}
                          onDrop={(event) => handleLessonDrop(event, module.module_id, lesson.lesson_id)}
                        >
                          <button
                            type="button"
                            className="curriculum-lesson-drag"
                            draggable
                            onDragStart={(event) => handleLessonDragStart(event, module.module_id, lesson.lesson_id)}
                            onDragEnd={() => setDragState(null)}
                            aria-label={`Drag ${lessonModel.title || 'lesson'}`}
                            title="Drag to reorder lesson"
                          >
                            <GripVertical className="w-4 h-4" />
                          </button>
                          <div className="curriculum-lesson-content">
                            <div className="curriculum-lesson-head">
                              <button
                                type="button"
                                className="curriculum-lesson-toggle"
                                onClick={() => toggleLessonExpanded(lesson.lesson_id)}
                              >
                                <span className="curriculum-lesson-toggle-title">{lessonModel.title || 'Untitled lesson'}</span>
                                <span className="curriculum-lesson-toggle-meta">
                                  {lessonModel.is_preview ? 'Preview' : 'Member only'}
                                  {lessonModel.duration_minutes > 0 ? ` | ${lessonModel.duration_minutes}m` : ''}
                                </span>
                                {lessonIsExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </button>
                              <div className="curriculum-lesson-edit-actions">
                                {lessonIsEditing ? (
                                  <button
                                    type="button"
                                    className="btn-secondary btn-compact"
                                    onClick={() => saveLessonEditing(lesson.lesson_id)}
                                    disabled={lessonIsSaving}
                                  >
                                    <Save className="w-4 h-4" /> {lessonIsSaving ? 'Saving...' : 'Save'}
                                  </button>
                                ) : (
                                  <button type="button" className="btn-secondary btn-compact" onClick={() => startLessonEditing(lesson.lesson_id)}>
                                    <Pencil className="w-4 h-4" /> Edit
                                  </button>
                                )}
                              </div>
                            </div>
                            {lessonIsExpanded && (
                              <>
                            <input
                              className="curriculum-lesson-title"
                              value={lessonModel.title}
                              onChange={(e) => updateLessonLocal(lesson.lesson_id, { title: e.target.value })}
                              placeholder="Lesson title"
                              disabled={!lessonIsEditing}
                            />
                            <div className="lesson-editor-grid">
                              <div className="lesson-editor-panel">
                                <div className="lesson-editor-panel-header">
                                  <h5><Video className="w-4 h-4" /> Media</h5>
                                  <span className="lesson-security-note">
                                    <Shield className="w-3 h-3" />
                                    Private uploads use signed URLs
                                  </span>
                                </div>

                                <div className="form-field">
                                  <label>Media Type</label>
                                  <SelectMenu
                                    value={normalizeMediaType(lessonModel)}
                                    options={LESSON_MEDIA_OPTIONS}
                                    onChange={(value) => {
                                      if (!lessonIsEditing) return;
                                      updateLessonLocal(lesson.lesson_id, { media_type: value });
                                    }}
                                    disabled={!lessonIsEditing}
                                    compact
                                  />
                                </div>

                                {normalizeMediaType(lessonModel) === 'video_url' && (
                                  <input
                                    className="curriculum-lesson-video"
                                    value={lessonModel.video_url || ''}
                                    onChange={(e) => updateLessonLocal(lesson.lesson_id, { video_url: e.target.value })}
                                    placeholder="Video URL (YouTube, Vimeo, or direct file)"
                                    disabled={!lessonIsEditing}
                                  />
                                )}

                                {normalizeMediaType(lessonModel) === 'image_url' && (
                                  <input
                                    className="curriculum-lesson-video"
                                    value={lessonModel.image_url || ''}
                                    onChange={(e) => updateLessonLocal(lesson.lesson_id, { image_url: e.target.value })}
                                    placeholder="Image URL"
                                    disabled={!lessonIsEditing}
                                  />
                                )}

                                {(normalizeMediaType(lessonModel) === 'video_asset' || normalizeMediaType(lessonModel) === 'image_asset') && (
                                  <div className="lesson-upload-block">
                                    <label className="btn-secondary btn-compact lesson-upload-btn">
                                      <Upload className="w-4 h-4" />
                                      {uploadingKey === `${lesson.lesson_id}:${normalizeMediaType(lessonModel) === 'video_asset' ? 'lesson_video' : 'lesson_image'}`
                                        ? 'Uploading...'
                                        : normalizeMediaType(lessonModel) === 'video_asset' ? 'Upload Video' : 'Upload Image'}
                                      <input
                                        type="file"
                                        accept={normalizeMediaType(lessonModel) === 'video_asset' ? 'video/*' : 'image/*'}
                                        disabled={!lessonIsEditing || uploadingKey === `${lesson.lesson_id}:${normalizeMediaType(lessonModel) === 'video_asset' ? 'lesson_video' : 'lesson_image'}`}
                                        onChange={(event) => {
                                          const file = event.target.files?.[0];
                                          if (file) {
                                            handleUploadLessonMedia(
                                              lesson,
                                              normalizeMediaType(lessonModel) === 'video_asset' ? 'video' : 'image',
                                              file
                                            );
                                          }
                                          event.target.value = '';
                                        }}
                                      />
                                    </label>
                                    {lessonModel.media_path && (
                                      <div className="lesson-upload-meta">
                                        <span>{lessonModel.media_filename || lessonModel.media_path.split('/').pop()}</span>
                                        {lessonModel.media_content_type && <span>{lessonModel.media_content_type}</span>}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>

                              <div className="lesson-editor-panel">
                                <div className="lesson-editor-panel-header">
                                  <h5><FileText className="w-4 h-4" /> Text Content</h5>
                                </div>
                                    <textarea
                                      className="curriculum-lesson-text"
                                      rows="6"
                                      value={lessonModel.content_md || ''}
                                      onChange={(e) => updateLessonLocal(lesson.lesson_id, { content_md: e.target.value })}
                                      onBlur={async (e) => {
                                        if (!lessonIsEditing) return;
                                        try {
                                          await updateLesson(lesson.lesson_id, { content_md: e.target.value });
                                        } catch (_) {
                                          // updateLesson already sets user-facing error banner
                                        }
                                      }}
                                      placeholder="Add instructions, notes, and links for this lesson..."
                                      disabled={!lessonIsEditing}
                                    />
                              </div>
                            </div>

                            <div className="lesson-editor-panel">
                              <div className="lesson-editor-panel-header">
                                <h5><Paperclip className="w-4 h-4" /> Downloads</h5>
                              </div>
                              <div className="lesson-upload-row">
                                <label className="btn-secondary btn-compact lesson-upload-btn">
                                  <Upload className="w-4 h-4" />
                                  {uploadingKey === `${lesson.lesson_id}:attachment` ? 'Uploading...' : 'Upload File'}
                                  <input
                                    type="file"
                                    disabled={!lessonIsEditing || uploadingKey === `${lesson.lesson_id}:attachment`}
                                    onChange={(event) => {
                                      const file = event.target.files?.[0];
                                      if (file) {
                                        handleUploadLessonAttachment(lesson, file);
                                      }
                                      event.target.value = '';
                                    }}
                                  />
                                </label>
                              </div>
                              <div className="lesson-attachment-list">
                                {(lessonModel.attachments || []).map((attachment) => {
                                  const attachmentId = attachment.id || attachment.path;
                                  return (
                                    <div key={attachmentId} className="lesson-attachment-item">
                                      <span className="name">{attachment.name || attachment.path}</span>
                                      <span className="meta">{formatFileSize(attachment.size_bytes)}</span>
                                      <button
                                        type="button"
                                        className="danger"
                                        disabled={!lessonIsEditing}
                                        onClick={() => removeLessonAttachment(lesson.lesson_id, attachmentId)}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  );
                                })}
                                {(lessonModel.attachments || []).length === 0 && (
                                  <div className="lesson-empty-row">No files uploaded yet.</div>
                                )}
                              </div>
                            </div>

                            <div className="lesson-editor-panel">
                              <div className="lesson-editor-panel-header">
                                <h5><Link2 className="w-4 h-4" /> Resource Links</h5>
                                <button
                                  type="button"
                                  className="btn-secondary btn-compact"
                                  onClick={() => addResourceLink(lesson.lesson_id)}
                                  disabled={!lessonIsEditing}
                                >
                                  <Plus className="w-4 h-4" /> Add Link
                                </button>
                              </div>
                              <div className="lesson-resource-list">
                                {(lessonModel.resource_links || []).map((resource) => {
                                  const resourceId = resource.id || `${resource.resource_type}-${resource.resource_id}-${resource.label}`;
                                  const resolvedPath = getInternalResourcePath(resource);
                                  return (
                                    <div key={resourceId} className="lesson-resource-item">
                                      <input
                                        className="curriculum-lesson-video"
                                        value={resource.label || ''}
                                        onChange={(e) => stageResourceLink(lesson.lesson_id, resourceId, { label: e.target.value })}
                                        placeholder="Label (e.g., Prompt Template)"
                                        disabled={!lessonIsEditing}
                                      />
                                      <div className="lesson-resource-row">
                                        <SelectMenu
                                          value={resource.resource_type || 'external'}
                                          options={RESOURCE_LINK_TYPE_OPTIONS}
                                          onChange={(value) => {
                                            if (!lessonIsEditing) return;
                                            stageResourceLink(lesson.lesson_id, resourceId, { resource_type: value });
                                          }}
                                          disabled={!lessonIsEditing}
                                          compact
                                        />
                                        {(resource.resource_type || 'external') === 'external' ? (
                                          <input
                                            className="curriculum-lesson-video"
                                            value={resource.url || ''}
                                            onChange={(e) => stageResourceLink(lesson.lesson_id, resourceId, { url: e.target.value })}
                                            placeholder="https://..."
                                            disabled={!lessonIsEditing}
                                          />
                                        ) : (
                                          <div className="lesson-internal-link-fields">
                                            <input
                                              className="curriculum-lesson-video"
                                              value={resource.resource_id || ''}
                                              onChange={(e) => stageResourceLink(lesson.lesson_id, resourceId, { resource_id: e.target.value })}
                                              placeholder="Resource ID"
                                              disabled={!lessonIsEditing}
                                            />
                                            {resource.resource_type === 'folder' && (
                                              <input
                                                className="curriculum-lesson-video"
                                                value={resource.project_id || ''}
                                                onChange={(e) => stageResourceLink(lesson.lesson_id, resourceId, { project_id: e.target.value })}
                                                placeholder="Project ID (required for folders)"
                                                disabled={!lessonIsEditing}
                                              />
                                            )}
                                            <div className="lesson-internal-link-preview">
                                              {resolvedPath ? resolvedPath : 'Add required IDs to generate route'}
                                            </div>
                                          </div>
                                        )}
                                        <button
                                          type="button"
                                          className="danger"
                                          disabled={!lessonIsEditing}
                                          onClick={() => removeResourceLink(lesson.lesson_id, resourceId)}
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                                {(lessonModel.resource_links || []).length === 0 && (
                                  <div className="lesson-empty-row">No resource links yet.</div>
                                )}
                              </div>
                            </div>

                            <div className="curriculum-lesson-options">
                              <label>
                                <input
                                  type="checkbox"
                                  checked={lessonModel.is_preview || false}
                                  onChange={(e) => updateLessonLocal(lesson.lesson_id, { is_preview: e.target.checked })}
                                  disabled={!lessonIsEditing}
                                />
                                Free Preview
                              </label>
                              <input
                                type="number"
                                className="curriculum-lesson-duration"
                                value={lessonModel.duration_minutes || ''}
                                onChange={(e) => updateLessonLocal(lesson.lesson_id, { duration_minutes: parseInt(e.target.value, 10) || 0 })}
                                placeholder="Duration (min)"
                                disabled={!lessonIsEditing}
                              />
                            </div>
                              </>
                            )}
                          </div>
                          <button type="button" className="curriculum-lesson-delete" onClick={() => deleteLesson(lesson.lesson_id)}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      );
                      })}
                      
                      {(module.lessons || []).length === 0 && (
                        <div className="curriculum-lessons-empty">
                          No lessons yet. Click "+ Lesson" to add one.
                        </div>
                      )}
                    </div>
                  </div>
                );
                })}
              </div>
            )}
          </div>
        )}
        
        {/* Pricing Tab */}
        {activeTab === 'pricing' && (
          <div className="course-builder-form course-builder-form--pricing">
            <div className="form-section">
              <h3>Pricing</h3>
              
              <div className="form-field">
                <label>Price (USD)</label>
                <input
                  className="input-glass"
                  type="number"
                  min="0"
                  step="1"
                  value={course.price_cents ? course.price_cents / 100 : ''}
                  onChange={(e) => setCourse(prev => ({ 
                    ...prev, 
                    price_cents: e.target.value ? Math.round(parseFloat(e.target.value) * 100) : 0 
                  }))}
                  placeholder="0 for free"
                />
                <p className="form-hint">Enter 0 for a free course</p>
              </div>
            </div>
            
            <div className="form-section">
              <h3>Included with Subscription Tiers</h3>
              <p className="form-hint mb-4">
                Users on these tiers get free access even if the course has a price.
              </p>
              
              <div className="tier-checkboxes">
                {TIER_OPTIONS.map(tier => (
                  <label key={tier.value} className="tier-checkbox">
                    <input
                      type="checkbox"
                      checked={(course.included_tiers || []).includes(tier.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setCourse(prev => ({
                            ...prev,
                            included_tiers: [...(prev.included_tiers || []), tier.value]
                          }));
                        } else {
                          setCourse(prev => ({
                            ...prev,
                            included_tiers: (prev.included_tiers || []).filter(t => t !== tier.value)
                          }));
                        }
                      }}
                    />
                    <span>{tier.label}</span>
                  </label>
                ))}
              </div>
              
              <div className="pricing-summary">
                <h4>Access Summary</h4>
                {course.price_cents === 0 ? (
                  <p><Check className="w-4 h-4 text-green-500" /> This course is free for everyone</p>
                ) : (
                  <>
                    <p>Price: <strong>{formatPrice(course.price_cents)}</strong></p>
                    {(course.included_tiers || []).length > 0 ? (
                      <p><Check className="w-4 h-4 text-green-500" /> Free for: {course.included_tiers.join(', ')} users</p>
                    ) : (
                      <p>All users must pay to access</p>
                    )}
                  </>
                )}
              </div>
            </div>
            
            <div className="form-section form-section--wide">
              <h3>Tags</h3>
              <div className="form-tags">
                {(course.tags || []).map((tag, i) => (
                  <span key={i} className="form-tag">
                    {tag}
                    <button onClick={() => setCourse(prev => ({
                      ...prev,
                      tags: prev.tags.filter((_, idx) => idx !== i)
                    }))}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                <input
                  className="form-tag-input"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Add tag..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newTag.trim()) {
                      setCourse(prev => ({
                        ...prev,
                        tags: [...(prev.tags || []), newTag.trim()]
                      }));
                      setNewTag('');
                    }
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== MAIN EXPORT ====================
export default function CoursesPage() {
  const { courseId } = useParams();
  const location = window.location.pathname;
  
  // Route to appropriate view
  if (location.includes('/builder') || courseId === 'new') {
    return <CourseBuilder />;
  }
  
  if (location.includes('/learn')) {
    return <CoursePlayer />;
  }
  
  if (courseId && courseId !== 'new') {
    // Check if it's edit mode
    if (location.includes('/edit')) {
      return <CourseBuilder />;
    }
    return <CourseDetail />;
  }
  
  return <CourseCatalog />;
}
